import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material'
import {
  Close,
  Person,
  Business,
  Schedule,
  CheckCircle,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Assignment,
  LocationOn,
  CalendarToday,
  AccessTime,
  Notes,
  FilePresent
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'

const MovementDetail = ({ open, onClose, movement }) => {
  if (!movement) return null

  const getStatusChip = (movement) => {
    let actualStatus = 'ISSUED'
    let color = 'info'
    let icon = <Schedule fontSize="small" />

    if (movement.isReturned && movement.condition === 'CANCELLED') {
      actualStatus = 'CANCELLED'
      color = 'error'
      icon = <CancelIcon fontSize="small" />
    } else if (movement.isReturned) {
      actualStatus = 'RETURNED'
      color = 'success'
      icon = <CheckCircle fontSize="small" />
    }

    return (
      <Chip
        label={actualStatus}
        color={color}
        size="small"
        icon={icon}
      />
    )
  }

  const getPriorityChip = (priority) => {
    const config = {
      LOW: { color: 'default' },
      NORMAL: { color: 'primary' },
      HIGH: { color: 'warning' },
      URGENT: { color: 'error' }
    }
    
    return (
      <Chip
        label={priority || 'NORMAL'}
        color={config[priority]?.color || 'primary'}
        size="small"
        variant="outlined"
      />
    )
  }

  const isOverdue = () => {
    if (movement.isReturned) return false
    return movement.expectedReturnDate && new Date() > parseISO(movement.expectedReturnDate)
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            File Movement Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* File Information */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="File Information" 
                avatar={<FilePresent color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      File Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {movement.mrdFile?.fileNumber || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Patient Name
                    </Typography>
                    <Typography variant="body1">
                      {movement.mrdFile?.patientName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Admission Date
                    </Typography>
                    <Typography variant="body1">
                      {movement.mrdFile?.admissionDate 
                        ? format(parseISO(movement.mrdFile.admissionDate), 'MMM dd, yyyy')
                        : 'N/A'
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Request Information */}
          {movement.relatedRequest && (
            <Grid item xs={12}>
              <Card>
                <CardHeader 
                  title="Related Request" 
                  avatar={<Assignment color="primary" />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Request Number
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">
                        #{movement.relatedRequest.requestNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Request Type
                      </Typography>
                      <Typography variant="body1">
                        {movement.relatedRequest.requestType}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Requested By
                      </Typography>
                      <Typography variant="body1">
                        {movement.relatedRequest.requestedBy}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Movement Details */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Movement Details" 
                avatar={<LocationOn color="primary" />}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <Business fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Movement Type"
                          secondary={movement.movementType?.replace(/_/g, ' ') || 'N/A'}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <LocationOn fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="From → To"
                          secondary={`${movement.fromLocation || 'N/A'} → ${movement.toLocation || 'N/A'}`}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <Person fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Issued To"
                          secondary={movement.issuedTo || 'N/A'}
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <Schedule fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Status"
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {getStatusChip(movement)}
                              {isOverdue() && (
                                <Chip
                                  label="OVERDUE"
                                  color="error"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <Assignment fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Priority"
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {getPriorityChip(movement.priority)}
                            </Box>
                          }
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <Person fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Issued By"
                          secondary={movement.issuedBy?.name || 'N/A'}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Timeline */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Timeline" 
                avatar={<AccessTime color="primary" />}
              />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarToday fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Issued Date"
                      secondary={format(parseISO(movement.issuedAt || movement.createdAt), 'MMM dd, yyyy HH:mm')}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <Schedule fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Expected Return Date"
                      secondary={
                        movement.expectedReturnDate 
                          ? format(parseISO(movement.expectedReturnDate), 'MMM dd, yyyy')
                          : 'Not specified'
                      }
                    />
                  </ListItem>
                  
                  {movement.isReturned && movement.returnedAt && (
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Returned Date"
                        secondary={format(parseISO(movement.returnedAt), 'MMM dd, yyyy HH:mm')}
                      />
                    </ListItem>
                  )}
                  
                  {movement.returnedBy && (
                    <ListItem>
                      <ListItemIcon>
                        <Person fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Returned By"
                        secondary={movement.returnedBy.name || 'N/A'}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Condition & Remarks */}
          {(movement.condition || movement.remarks || movement.conditionRemarks) && (
            <Grid item xs={12}>
              <Card>
                <CardHeader 
                  title="Additional Information" 
                  avatar={<Notes color="primary" />}
                />
                <CardContent>
                  {movement.condition && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        File Condition
                      </Typography>
                      <Chip
                        label={movement.condition}
                        color={movement.condition === 'GOOD' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                  
                  {movement.conditionRemarks && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Condition Remarks
                      </Typography>
                      <Typography variant="body2">
                        {movement.conditionRemarks}
                      </Typography>
                    </Box>
                  )}
                  
                  {movement.remarks && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Remarks
                      </Typography>
                      <Typography variant="body2">
                        {movement.remarks}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MovementDetail